/**
 * EmptyState — Contextual placeholder displayed when a table has no rows.
 *
 * Three variants:
 *  - EmptyStateNoData:    Initial empty state — no records exist yet
 *  - EmptyStateNoResults: Filter/search returned no matches
 *  - EmptyStateError:     Data fetch failed
 */

import React from 'react';

import { Button } from '@/lib/base/buttons/button';

// ---------------------------------------------------------------------------
// Illustrations (inline SVG to avoid asset dependencies)
// ---------------------------------------------------------------------------

const NoDataIllustration = () => (
  <svg
    width='64'
    height='64'
    viewBox='0 0 64 64'
    fill='none'
    aria-hidden='true'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='8'
      y='16'
      width='48'
      height='36'
      rx='4'
      stroke='var(--color-border-secondary)'
      strokeWidth='2'
      fill='var(--color-bg-secondary)'
    />
    <rect
      x='8'
      y='16'
      width='48'
      height='10'
      rx='4'
      fill='var(--color-bg-tertiary)'
    />
    <rect
      x='14'
      y='34'
      width='20'
      height='3'
      rx='1.5'
      fill='var(--color-border-secondary)'
    />
    <rect
      x='14'
      y='42'
      width='14'
      height='3'
      rx='1.5'
      fill='var(--color-border-secondary)'
    />
  </svg>
);

const ErrorIllustration = () => (
  <svg
    width='64'
    height='64'
    viewBox='0 0 64 64'
    fill='none'
    aria-hidden='true'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M32 8L56 52H8L32 8Z'
      stroke='var(--color-border-secondary)'
      strokeWidth='2'
      fill='var(--color-bg-secondary)'
      strokeLinejoin='round'
    />
    <rect
      x='30'
      y='24'
      width='4'
      height='14'
      rx='2'
      fill='var(--color-text-tertiary)'
    />
    <rect
      x='30'
      y='42'
      width='4'
      height='4'
      rx='2'
      fill='var(--color-text-tertiary)'
    />
  </svg>
);

// ---------------------------------------------------------------------------
// Shared wrapper
// ---------------------------------------------------------------------------

const EmptyStateWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className='flex flex-col items-center justify-center py-16 px-8 text-center'>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// EmptyStateNoData
// ---------------------------------------------------------------------------

/** Naive English pluralization — handles common CRM entity type names. */
function pluralize(word: string): string {
  if (word.endsWith('y') && !/[aeiou]y$/i.test(word)) {
    return `${word.slice(0, -1)}ies`;
  }
  if (
    word.endsWith('s') ||
    word.endsWith('x') ||
    word.endsWith('z') ||
    word.endsWith('ch') ||
    word.endsWith('sh')
  ) {
    return `${word}es`;
  }
  return `${word}s`;
}

interface EmptyStateNoDataProps {
  /** Entity type label, e.g. "contact" or "company" */
  entityType?: string;
  /** Custom heading override */
  heading?: string;
  /** Custom description override */
  description?: string;
  /** Callback for the "Create first" CTA button */
  onCreateFirst?: () => void;
  /**
   * Render the "Create first" CTA disabled rather than active. Use when create
   * is momentarily unavailable (e.g. a default value still resolving) so the
   * button stays visible but can't be clicked into a silent no-op.
   */
  createFirstDisabled?: boolean;
}

export const EmptyStateNoData = ({
  entityType = 'record',
  heading,
  description,
  onCreateFirst,
  createFirstDisabled,
}: EmptyStateNoDataProps) => {
  const pluralType = pluralize(entityType);
  const headingText = heading ?? `No ${pluralType} yet`;
  const descriptionText =
    description ?? `Get started by creating your first ${entityType}.`;

  return (
    <EmptyStateWrapper>
      <NoDataIllustration />
      <h3 className='mt-4 text-heading-4 text-[var(--color-text-primary)]'>
        {headingText}
      </h3>
      <p className='mt-2 text-body-small text-[var(--color-text-secondary)] max-w-xs'>
        {descriptionText}
      </p>
      {onCreateFirst && (
        <Button
          color='primary'
          size='md'
          onClick={onCreateFirst}
          isDisabled={createFirstDisabled}
          aria-label={`Create your first ${entityType}`}
          className='mt-6'
        >
          Create your first {entityType}
        </Button>
      )}
    </EmptyStateWrapper>
  );
};

// ---------------------------------------------------------------------------
// EmptyStateNoResults
// ---------------------------------------------------------------------------

interface EmptyStateNoResultsProps {
  /** Custom heading override */
  heading?: string;
  /** Custom description override */
  description?: string;
  /** Callback for the "Clear filters" CTA button */
  onClearFilters?: () => void;
}

export const EmptyStateNoResults = ({
  heading,
  description,
  onClearFilters,
}: EmptyStateNoResultsProps) => {
  const headingText = heading ?? 'Nothing matches the current filters.';
  const descriptionText = description;

  return (
    <EmptyStateWrapper>
      <p className='text-sm text-[var(--color-text-tertiary)]'>{headingText}</p>
      {descriptionText && (
        <p className='mt-2 text-sm text-[var(--color-text-tertiary)] max-w-xs'>
          {descriptionText}
        </p>
      )}
      {onClearFilters && (
        <Button
          color='secondary'
          size='md'
          onClick={onClearFilters}
          aria-label='Clear filters'
          className='mt-6'
        >
          Clear filters
        </Button>
      )}
    </EmptyStateWrapper>
  );
};

// ---------------------------------------------------------------------------
// EmptyStateError
// ---------------------------------------------------------------------------

interface EmptyStateErrorProps {
  /** Error message to display */
  errorMessage?: string;
  /** Custom heading override */
  heading?: string;
  /** Callback for the "Retry" CTA button */
  onRetry?: () => void;
}

export const EmptyStateError = ({
  errorMessage,
  heading,
  onRetry,
}: EmptyStateErrorProps) => {
  const headingText = heading ?? 'Something went wrong';
  const descriptionText =
    errorMessage ?? 'An error occurred while loading data. Please try again.';

  return (
    <EmptyStateWrapper>
      <ErrorIllustration />
      <h3 className='mt-4 text-heading-4 text-[var(--color-text-primary)]'>
        {headingText}
      </h3>
      <p className='mt-2 text-body-small text-[var(--color-text-secondary)] max-w-xs'>
        {descriptionText}
      </p>
      {onRetry && (
        <Button
          color='primary'
          size='md'
          onClick={onRetry}
          aria-label='Retry loading data'
          className='mt-6'
        >
          Retry
        </Button>
      )}
    </EmptyStateWrapper>
  );
};
